import { ssm, SsmDataChannel } from './ssm';

const textDecoder = new TextDecoder();
const textEncoder = new TextEncoder();

// AWS SSM caps stream-data payloads (session-manager-plugin StreamDataPayloadSize).
const PORT_FORWARD_CHUNK = 1024;

/**
 * SsmTunnel — transport-only wrapper around the AWS SSM "agent message" data
 * channel. It owns the WebSocket, runs the token + KMS handshake, ACKs and
 * reorders inbound messages, and exposes a duplex byte pipe:
 *
 *   tunnel.onData(bytes => ...)   // in-order, decrypted payload bytes (opaque)
 *   tunnel.onState(state => ...)  // 'open' | 'ready' | 'closed' | 'paused' |
 *                                 // 'resumed' | 'resize-request'
 *   tunnel.write(bytes)           // app payload -> input_stream_data
 *   tunnel.sendControl(opts)      // shell terminal geometry (init/resize)
 *
 * This is extracted verbatim from the shell console in SsmDialog.jsx so the
 * shell and the new RDP path can share one transport. Encryption is gated on
 * the agent actually requesting KMSEncryption in the handshake, so a
 * port-forwarding session (which typically does not) runs unencrypted with no
 * code change.
 *
 * Port-forward mode (`new SsmTunnel(session, { portForward: true })`, used by
 * RDP over AWS-StartPortForwardingSession): outbound `write` carries raw TCP
 * bytes, chunked to the SSM payload cap and serialized to keep the stream
 * strictly ordered. The stream sequence is SHARED with the handshake response
 * (so the first data frame is seq 1) and Flags are always 0, matching AWS's
 * session-manager-plugin. The agent selects basic (raw TCP) vs multiplexed
 * (smux) forwarding from the handshake-response ClientVersion; ssm.js reports a
 * pre-1.1.70 version to stay on basic, which is what this carries — we do not
 * implement smux. The handshake, ACKs, and inbound reorder are shared with the
 * shell. Inbound control `Flag` payloads (ConnectToPortError / TerminateSession
 * / DisconnectToPort) end the session.
 */
export default class SsmTunnel {
  constructor(session, options = {}) {
    this.session = session;
    this.portForward = !!options.portForward;
    this.socket = null;
    this.channel = null;
    this.buffer = [];
    this.lastSeqNum = -1;
    this._handshakeResponded = false;
    this._writeQueue = Promise.resolve();
    this.paused = false;
    this.keepaliveInterval = null;
    this._dataCb = null;
    this._stateCb = null;
    this._errorCb = null;
  }

  onData(cb) {
    this._dataCb = cb;
    return this;
  }

  onState(cb) {
    this._stateCb = cb;
    return this;
  }

  onError(cb) {
    this._errorCb = cb;
    return this;
  }

  _emitData(payload) {
    if (this._dataCb) this._dataCb(payload);
  }

  _emitState(state) {
    if (this._stateCb) this._stateCb(state);
  }

  _emitError(err) {
    if (this._errorCb) this._errorCb(err);
  }

  open() {
    if (!this.session || !this.session.streamUrl) {
      this._emitError(new Error('no_stream_url'));
      return;
    }
    this.socket = new WebSocket(this.session.streamUrl);
    this.socket.binaryType = 'arraybuffer';

    this.socket.addEventListener('open', () => {
      this.channel = new SsmDataChannel(this.socket);
      // Pin the agent to basic (non-smux) port-forwarding; see ssm.js.
      if (this.portForward) this.channel.clientVersion = '1.1.61.0';
      this._emitState({ type: 'open' });
      this.channel.init({ token: this.session.tokenValue });
    });
    this.socket.addEventListener('close', () => {
      this._emitState({ type: 'closed' });
    });
    this.socket.addEventListener('error', () => {
      this._emitError(new Error('ssm_socket_error'));
    });
    this.socket.addEventListener('message', event => this._onMessage(event));

    // Keep the gateway connection alive (mirrors the shell's 5-min ping).
    this.keepaliveInterval = setInterval(() => {
      if (!this.paused && this.socket && this.channel) {
        this.socket.send('__ping__');
      }
    }, 300000);
  }

  // App payload -> input_stream_data. Alias `sendText` preserves the parent
  // admin tooling (insertCommand + Commands menu) that calls
  // parentSsmDataChannelRef.current.sendText(...) directly.
  write(bytes) {
    if (!this.channel) return undefined;
    if (!this.portForward) {
      // Shell: keystroke-sized, fire-and-forget.
      return this.channel.sendText(bytes);
    }
    // Port-forward: chunk to the SSM payload cap and serialize so the stream
    // sequence numbers stay strictly ordered across concurrent writes.
    this._writeQueue = this._writeQueue.then(() =>
      this._writePortForward(bytes),
    );
    return this._writeQueue;
  }

  async _writePortForward(bytes) {
    const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    for (let offset = 0; offset < data.length; offset += PORT_FORWARD_CHUNK) {
      const chunk = data.subarray(
        offset,
        Math.min(offset + PORT_FORWARD_CHUNK, data.length),
      );
      const payload = this.channel.requireEncryption
        ? await this.channel.encryptExecText(chunk)
        : chunk;
      // Raw TCP data: PayloadType Output(1), Flags 0. The outbound sequence is
      // SHARED with the handshake response (already an input_stream_data message
      // at seq 0), so the first data frame is seq 1 — a separate counter reuses
      // seq 0 and the agent silently drops the frame as a duplicate.
      this.socket.send(
        this.channel.buildDataMessage(
          payload,
          this.channel.messageSequenceNumber,
          0,
        ),
      );
      this.channel.messageSequenceNumber += 1;
    }
  }

  sendText(bytes) {
    return this.write(bytes);
  }

  // Shell-only: terminal geometry (init/resize). RDP never calls this.
  sendControl(options) {
    if (this.channel) this.channel.sendInitMessage(options);
  }

  close() {
    if (this.keepaliveInterval) {
      clearInterval(this.keepaliveInterval);
      this.keepaliveInterval = null;
    }
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  async _onMessage(event) {
    // MGS sends binary "agent messages"; a *string* frame is an out-of-band
    // control/error from the gateway (e.g. a rejected/expired token). Don't feed
    // it to the binary decoder, which would silently produce a garbage message.
    if (typeof event.data === 'string') {
      return;
    }
    const agentMessage = ssm.decode(event.data);

    // The agent ACKs our messages; don't ACK an ACK.
    if (agentMessage.messageType === 'acknowledge') {
      return;
    }

    this.channel.sendACK(agentMessage);

    if (agentMessage.messageType === 'channel_closed') {
      this._emitState({ type: 'closed' });
      return;
    }

    // Port-forward control flag: ConnectToPortError(3) / TerminateSession(2) /
    // DisconnectToPort(1).
    if (agentMessage.payloadType === 10) {
      const p = agentMessage.payload;
      const flag =
        p && p.length >= 4
          ? new DataView(p.buffer, p.byteOffset, 4).getUint32(0, false)
          : 0;
      if (flag === 3) {
        this._emitError(new Error('connect_to_port_error'));
      }
      this._emitState({ type: 'closed', flag });
      return;
    }

    if (agentMessage.payloadType === 'pause_publication') {
      this.paused = true;
      this._emitState({ type: 'paused' });
    }
    if (agentMessage.payloadType === 'start_publication') {
      this.paused = false;
      this._emitState({ type: 'resumed' });
    }

    // Handshake request: always respond (acking SessionType, and setting up KMS
    // if the agent asked for it). Without a response the agent never completes
    // the handshake and MGS closes the channel — and port-forwarding sessions
    // usually have no KMSEncryption action, only SessionType.
    if (agentMessage.payloadType === 5 && !this._handshakeResponded) {
      this._handshakeResponded = true;
      const jsonMessage = JSON.parse(textDecoder.decode(agentMessage.payload));
      const reqActions = jsonMessage['RequestedClientActions'];
      const kmsAction = reqActions.find(x => x.ActionType === 'KMSEncryption');
      if (kmsAction) {
        this.channel.requireEncryption = true;
        this._setupExecKms(reqActions);
      } else {
        this.channel.sendHandshakeResponse(reqActions, null);
      }
    }

    // KMS challenge.
    if (agentMessage.payloadType === 8) {
      this._handleKmsChallengeRequest(agentMessage);
      this.lastSeqNum = agentMessage.sequenceNumber;
    }

    // Handshake complete.
    if (agentMessage.payloadType === 7) {
      let customerMessage;
      try {
        customerMessage = JSON.parse(textDecoder.decode(agentMessage.payload))[
          'CustomerMessage'
        ];
      } catch (err) {
        // no customer message
      }
      this._emitState({ type: 'ready', customerMessage });
      this.lastSeqNum = agentMessage.sequenceNumber;
    }

    // Stream data.
    if (agentMessage.payloadType === 1) {
      this._handleData(agentMessage);
    } else if (agentMessage.payloadType === 17) {
      this._emitState({ type: 'resize-request' });
    }
  }

  async _handleData(agentMessage) {
    if (this.channel.requireEncryption) {
      agentMessage.payload = new Uint8Array(
        await this._decrypt(agentMessage.payload),
      );
    }
    if (agentMessage.sequenceNumber - 1 !== this.lastSeqNum) {
      this.buffer.push(agentMessage);
      if (this.buffer.length > 1024) {
        // Seq desync: incoming data isn't lining up with lastSeqNum, so nothing
        // ever flushes. Fail instead of growing until the tab OOMs.
        this._emitError(new Error('reorder_buffer_overflow'));
        this.close();
      }
    } else {
      this.lastSeqNum = agentMessage.sequenceNumber;
      this._emitData(agentMessage.payload);
      if (this.buffer.length > 0) {
        const length = this.buffer.length;
        for (let i = length - 1; i >= 0; i--) {
          if (this.buffer[i].sequenceNumber - 1 === this.lastSeqNum) {
            this.lastSeqNum = this.buffer[i].sequenceNumber;
            this._emitData(this.buffer[i].payload);
            this.buffer.splice(i, 1);
          }
        }
      }
    }
  }

  async _setupExecKms(reqActions) {
    await new Promise(resolve => setTimeout(resolve, 500));
    const keySize = 64;
    const ciphertextBlob = this.session.ciphertextBlob;

    const plaintextBytes = Uint8Array.from(atob(this.session.plaintext), c =>
      c.charCodeAt(0),
    );
    if (plaintextBytes.length !== keySize) {
      throw new Error(
        `Expected key size of ${keySize} bytes, got ${plaintextBytes.length}`,
      );
    }

    const decryptionKeyBytes = plaintextBytes.slice(0, keySize / 2);
    const encryptionKeyBytes = plaintextBytes.slice(keySize / 2, keySize);

    this.channel.decryptionKey = await crypto.subtle.importKey(
      'raw',
      decryptionKeyBytes,
      'AES-GCM',
      false,
      ['decrypt'],
    );
    this.channel.encryptionKey = await crypto.subtle.importKey(
      'raw',
      encryptionKeyBytes,
      'AES-GCM',
      false,
      ['encrypt'],
    );

    this.channel.sendHandshakeResponse(reqActions, ciphertextBlob);
  }

  async _handleKmsChallengeRequest(agentMessage) {
    const jsonMessage = JSON.parse(textDecoder.decode(agentMessage.payload));
    const challenge = jsonMessage['Challenge'];
    const challengeData = new Uint8Array(
      atob(challenge)
        .split('')
        .map(c => c.charCodeAt(0)),
    );
    const decryptedChallenge = await this._decrypt(challengeData);
    const encryptedChallenge = await this._encrypt(decryptedChallenge);
    const challengeResponseb64 = btoa(
      String.fromCharCode(...encryptedChallenge),
    );
    const challengeResponseJson = JSON.stringify({
      Challenge: challengeResponseb64,
    });
    this.channel.sendChallengeResponse(
      textEncoder.encode(challengeResponseJson),
    );
  }

  async _decrypt(ciphertext) {
    const nonce = new Uint8Array(ciphertext.slice(0, 12));
    const ciphertextNoNonce = new Uint8Array(
      ciphertext.slice(12, ciphertext.byteLength),
    );
    return window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: nonce },
      this.channel.decryptionKey,
      ciphertextNoNonce,
    );
  }

  async _encrypt(plaintext) {
    const nonce = window.crypto.getRandomValues(new Uint8Array(12));
    let ciphertext = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: nonce },
      this.channel.encryptionKey,
      plaintext,
    );
    ciphertext = new Uint8Array(ciphertext);
    const nonceAndCiphertext = new Uint8Array(
      nonce.byteLength + ciphertext.byteLength,
    );
    nonceAndCiphertext.set(nonce);
    nonceAndCiphertext.set(ciphertext, nonce.byteLength);
    return nonceAndCiphertext;
  }
}
